import React from 'react';
import UISectionWrapper, {
  UISectionHeader,
  UISectionBody,
  UISectionFooter,
  UIFormRow,
  UIFormLabels,
  UIFormFields,
  UIFormLabel
} from '@/components/shared/UISectionWrapper';
import UISectionTitle from '@/components/shared/UISectionTitle';
import UIInput from '@/components/shared/UIInput';
import UISelect from '@/components/shared/UISelect';
import UICheckbox from '@/components/shared/UICheckbox';
import UIButton from '@/components/shared/UIButton';
import UISpinner from '@/components/shared/UISpinner';
import { useProfile } from '@/hooks/useProfile';
import './TenantBillingInformationSection.scss';

export interface TenantBillingInformationSectionProps {
  className?: string;
}

const baseClass = 'TenantBillingInformationSection';

const accountTypeOptions = [
  { value: 'checking', label: 'Checking' },
  { value: 'savings', label: 'Savings' },
  { value: 'business', label: 'Business' },
];

const TenantBillingInformationSection: React.FC<TenantBillingInformationSectionProps> = ({ className = '' }) => {
  const {
    data,
    initialData,
    loading,
    handleChange,
    handleSave,
    handleClear,
    getAgreementValue,
    setAgreementValue,
    isFormValid,
  } = useProfile('billing');

  const handlePadAgreementChange = (checked: boolean) => {
    setAgreementValue('tenant_pad_authorization', checked);
  };

  const handleStoreAgreementChange = (checked: boolean) => {
    setAgreementValue('billing_authorization', checked);
  };

  const isValid = isFormValid();

  const hasChanges =
    data.tenant_pad_account_holder !== initialData.tenant_pad_account_holder ||
    data.tenant_pad_bank_name !== initialData.tenant_pad_bank_name ||
    data.tenant_pad_bank_branch_address !== initialData.tenant_pad_bank_branch_address ||
    data.tenant_pad_transit_number !== initialData.tenant_pad_transit_number ||
    data.tenant_pad_institution_number !== initialData.tenant_pad_institution_number ||
    data.tenant_pad_account_number !== initialData.tenant_pad_account_number ||
    data.tenant_pad_account_type !== initialData.tenant_pad_account_type ||
    data.tenant_pad_swift_bic_code !== initialData.tenant_pad_swift_bic_code ||
    data.billing_authorization !== initialData.billing_authorization ||
    data.tenant_pad_authorization !== initialData.tenant_pad_authorization;

  if (loading) {
    return (
      <div className={`${baseClass}__loading`} style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
        <UISpinner />
      </div>
    );
  }

  return (
    <UISectionWrapper
      className={`${baseClass} ${className}`}
      layout="form-horizontal"
      formPadding="large"
      labelWidth="200px"
      formGap="15px"
      align="stretch"
    >
      <UISectionHeader>
        <UISectionTitle
          title="Banking information"
          description="Update your banking information for pre-authorized debits."
        />
      </UISectionHeader>

      <UISectionBody>
        <UIFormRow>
          <UIFormLabels>
            <UIFormLabel>Account holder name</UIFormLabel>
            <UIFormLabel>Bank name</UIFormLabel>
            <UIFormLabel>Bank branch address</UIFormLabel>
            <UIFormLabel>Transit number (branch)</UIFormLabel>
            <UIFormLabel>Institution number</UIFormLabel>
            <UIFormLabel>Account number</UIFormLabel>
            <UIFormLabel>Type of account</UIFormLabel>
            <UIFormLabel>Swift/BIC code (if relevant)</UIFormLabel>
            <UIFormLabel>Date account was added</UIFormLabel>
            <UIFormLabel>Account verification status</UIFormLabel>
            <UIFormLabel>&nbsp;</UIFormLabel>
            <UIFormLabel>&nbsp;</UIFormLabel>
          </UIFormLabels>

          <UIFormFields>
            <UIInput
              placeholder="Account holder name"
              fullWidth
              value={data.tenant_pad_account_holder || ''}
              onChange={(e) => handleChange('tenant_pad_account_holder', e.target.value)}
            />
            <UIInput
              placeholder="Bank name"
              fullWidth
              value={data.tenant_pad_bank_name || ''}
              onChange={(e) => handleChange('tenant_pad_bank_name', e.target.value)}
            />
            <UIInput
              placeholder="Bank branch address"
              fullWidth
              value={data.tenant_pad_bank_branch_address || ''}
              onChange={(e) => handleChange('tenant_pad_bank_branch_address', e.target.value)}
            />
            <UIInput
              placeholder="5 digits"
              fullWidth
              value={data.tenant_pad_transit_number || ''}
              onChange={(e) => handleChange('tenant_pad_transit_number', e.target.value)}
            />
            <UIInput
              placeholder="3 digits"
              fullWidth
              value={data.tenant_pad_institution_number || ''}
              onChange={(e) => handleChange('tenant_pad_institution_number', e.target.value)}
            />
            <UIInput
              placeholder="Account number"
              fullWidth
              value={data.tenant_pad_account_number || ''}
              onChange={(e) => handleChange('tenant_pad_account_number', e.target.value)}
            />
            <UISelect
              options={accountTypeOptions}
              placeholder="Select type"
              fullWidth
              value={data.tenant_pad_account_type || ''}
              onChange={(e) => handleChange('tenant_pad_account_type', e.target.value)}
            />
            <UIInput
              placeholder="Swift/BIC code"
              fullWidth
              value={data.tenant_pad_swift_bic_code || ''}
              onChange={(e) => handleChange('tenant_pad_swift_bic_code', e.target.value)}
            />
            <UIInput placeholder="-" fullWidth disabled />
            <UIInput placeholder="-" fullWidth disabled />

            <div className={`${baseClass}__agreement-checkbox`}>
              <UICheckbox
                size="medium"
                checked={getAgreementValue('billing_authorization')}
                onChange={(e) => handleStoreAgreementChange(e.target.checked)}
                label="I authorize PropSavvy to store my billing information per PCI-DSS guidelines."
              />
            </div>
            <div className={`${baseClass}__agreement-checkbox`}>
              <UICheckbox
                size="medium"
                checked={getAgreementValue('tenant_pad_authorization')}
                onChange={(e) => handlePadAgreementChange(e.target.checked)}
                label="I authorize PropSavvy to debit the bank account identified above."
              />
            </div>
          </UIFormFields>
        </UIFormRow>
      </UISectionBody>

      <UISectionFooter>
        <div className={`${baseClass}__buttons`}>
          <UIButton
            variant="primary"
            onClick={handleSave}
            disabled={!isValid || !hasChanges}
          >
            Save
          </UIButton>
          <UIButton variant="primary" outline onClick={handleClear}>
            Clear
          </UIButton>
        </div>
      </UISectionFooter>
    </UISectionWrapper>
  );
};

export default TenantBillingInformationSection;